import React from 'react';
import styled from 'styled-components';

import {accountDetailsContactsDropdownRenderTooltip} from '../../../../../libs/contacts-manager/src/lib/ui/web/helpers/contactsDropdownHelpers';
import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {globalNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ContactOrAccountDetailsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AccordionSectionData} from '../../../../../libs/shared/ui/src/components/accordion/accordionSection';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {AccountAttributeUpdateAction} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {
  AccountDetailsSectionsEnum,
  AccountDetailsSectionsExpandState,
} from '../../../../models/api/localPreferencesModel';
import {AccountDetailsCardsState} from '../../../../store/panels/accounts/accountDetailsPanelState';
import {SearchContactsDropdownContainer} from '../../../common/dropdowns/search/searchContactsDropdownContainer';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  AccountDetailsTrackEventHandler,
  buildAccountDetailsEditPayload,
} from '../../tracking/accountDetailsTrackingHelpers';
import {AccountInformationContainer} from '../accountInformation/accountInformationContainer';
import {AddAccountInformationPopoverContainer} from '../accountInformation/addAccountInformationPopoverContainer';
import {AccountDetailsContactsContainer} from '../contacts/accountDetailsContactsContainer';
import {AccountDetailsConversationListContainer} from '../conversationList/accountDetailsConversationListContainer';

/*
 * Constants.
 */

const sectionAccordionPadding = 16;
const titleAccordionPadding = '13px 9px';

/*
 * Types.
 */

export type OnAccountDetailsSectionToggledHandler = (
  section: AccountDetailsSectionsEnum,
  isExpanded: boolean,
) => void;

interface BuildAccountDetailsSectionBaseOptions {
  panelId: ContactOrAccountDetailsPanelId;
  expandState: AccountDetailsSectionsExpandState;
  onAccountDetailsSectionToggled: OnAccountDetailsSectionToggledHandler;
}

export interface BuildAccountDetailsConversationSectionOptions extends BuildAccountDetailsSectionBaseOptions {
  conversationStatus?: AsyncStatusesEnum;
  conversationIds?: ReadonlyArray<number>;
  hasMoreConversations?: boolean;
  accountId?: number;
  domain?: string;
  onAccountDetailsSectionToggled: OnAccountDetailsSectionToggledHandler;
}

export interface BuildAccountInformationSectionOptions extends BuildAccountDetailsSectionBaseOptions {
  unsavedAccount: AccountModel;
  shouldDisableActions?: boolean;
  updateUnsavedAccount: (action: AccountAttributeUpdateAction) => void;
}

export interface BuildContactsSectionOptions extends BuildAccountDetailsSectionBaseOptions {
  cardsState?: AccountDetailsCardsState;
  account: AccountModel;
  localize: Localizer;
  addCardToAccount: (arg: number) => Promise<void>;
  onTrackEvent: AccountDetailsTrackEventHandler;
}

/*
 * Styles.
 */

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
`;

const StyledButton = styled(Button)`
  margin: -5px -12px -4px 0;
`;

/*
 * Helpers.
 */

export function buildAccountDetailsConversationSection(
  options: BuildAccountDetailsConversationSectionOptions,
): AccordionSectionData {
  const {
    panelId,
    expandState,
    conversationStatus,
    conversationIds,
    hasMoreConversations,
    accountId,
    domain,
    onAccountDetailsSectionToggled,
  } = options;
  return {
    id: AccountDetailsSectionsEnum.CONVERSATIONS,
    title: renderSectionTitle(<Trans id="account-details-conversations-title">Conversations</Trans>),
    isExpandedByDefault: expandState[AccountDetailsSectionsEnum.CONVERSATIONS],
    onSectionToggled: (isExpanded: boolean) => {
      onAccountDetailsSectionToggled(AccountDetailsSectionsEnum.CONVERSATIONS, isExpanded);
    },
    sectionPadding: sectionAccordionPadding,
    titlePadding: titleAccordionPadding,
    content: (
      <AccountDetailsConversationListContainer
        panelId={panelId}
        accountId={accountId}
        domain={domain}
        conversationLoadingStatus={conversationStatus}
        conversationIds={conversationIds ?? []}
        hasMoreConversations={hasMoreConversations}
      />
    ),
  };
}

export function buildAccountInformationSection(
  options: BuildAccountInformationSectionOptions,
): AccordionSectionData {
  const {
    panelId,
    expandState,
    unsavedAccount,
    shouldDisableActions,
    onAccountDetailsSectionToggled,
    updateUnsavedAccount,
  } = options;
  return {
    id: AccountDetailsSectionsEnum.ACCOUNT_INFORMATION,
    title: renderSectionTitle(<Trans id="account-details-account-information-title">About</Trans>),
    titleAction:
      expandState[AccountDetailsSectionsEnum.ACCOUNT_INFORMATION] && !shouldDisableActions ? (
        <AddAccountInformationPopoverContainer
          panelId={panelId}
          account={unsavedAccount}
          existingCustomAttributes={unsavedAccount.customFieldAttributes || []}
          onAddInformation={updateUnsavedAccount}
          buttonText={<Trans id="account-details-add-button-label">+ Add</Trans>}
        />
      ) : undefined,
    isExpandedByDefault: expandState[AccountDetailsSectionsEnum.ACCOUNT_INFORMATION],
    onSectionToggled: (isExpanded: boolean) => {
      onAccountDetailsSectionToggled(AccountDetailsSectionsEnum.ACCOUNT_INFORMATION, isExpanded);
    },
    sectionPadding: sectionAccordionPadding,
    titlePadding: titleAccordionPadding,
    content: (
      <AccountInformationContainer
        account={unsavedAccount}
        panelId={panelId}
        shouldDisableActions={shouldDisableActions}
        updateUnsavedAccount={updateUnsavedAccount}
      />
    ),
  };
}

export function buildContactsSection(options: BuildContactsSectionOptions): AccordionSectionData {
  const {
    panelId,
    expandState,
    cardsState,
    account,
    onAccountDetailsSectionToggled,
    localize,
    addCardToAccount,
    onTrackEvent,
  } = options;
  return {
    id: AccountDetailsSectionsEnum.CONTACTS,
    title: renderSectionTitle(<Trans id="account-details-contacts-title">Contacts</Trans>),
    titleAction: expandState[AccountDetailsSectionsEnum.CONTACTS]
      ? renderAddContactButton(account.id, localize, addCardToAccount, onTrackEvent)
      : undefined,
    isExpandedByDefault: expandState[AccountDetailsSectionsEnum.CONTACTS],
    onSectionToggled: (isExpanded: boolean) => {
      onAccountDetailsSectionToggled(AccountDetailsSectionsEnum.CONTACTS, isExpanded);
    },
    sectionPadding: sectionAccordionPadding,
    titlePadding: titleAccordionPadding,
    content: <AccountDetailsContactsContainer panelId={panelId} account={account} cardsState={cardsState} />,
  };
}

const renderSectionTitle = (text: React.ReactNode) => <StyledTitleDiv>{text}</StyledTitleDiv>;

const renderAddContactButton = (
  accountId: number,
  localize: Localizer,
  addCardToAccount: (arg: number) => Promise<void>,
  onTrackEvent: AccountDetailsTrackEventHandler,
) => (
  <PopoverCoordinator
    renderButton={(isOpen) => (
      <StyledButton
        variant={ButtonVariantsEnum.LINK}
        hasHover={isOpen}
        onClick={(event) => {
          event.preventDefault();
        }}
      >
        <Trans id="account-details-add-contact-button">+ Add</Trans>
      </StyledButton>
    )}
    renderModal={(popoverProps) => (
      <SearchContactsDropdownContainer
        popoverProps={popoverProps}
        searchPlaceholder={localize(
          defineMessage({
            id: 'account-details-search-contacts-placeholder',
            message: 'Search for a contact to add',
          }),
        )}
        filterSearchResults={(card) => card.namespace === globalNamespace}
        onConfirm={async (card) => {
          popoverProps.onRequestClose();
          await addCardToAccount(card.id);

          onTrackEvent(
            buildAccountDetailsEditPayload(
              accountId,
              AccountDetailsEditActionsEnum.ADD,
              AccountDetailsEditAttributesEnum.CONTACTS,
            ),
          );
        }}
        computeIsContactDisabled={(card) => Boolean(card.accountId)}
        renderTooltip={accountDetailsContactsDropdownRenderTooltip}
      />
    )}
  />
);
